import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { onboard, ApiError } from "@/lib/api";

export function PendingPage() {
  const { me, signOut, refresh } = useAuth();
  const [inviteCode, setInviteCode] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  async function submitOnboard(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await onboard({
        inviteCode: inviteCode || undefined,
        message: message || undefined,
      });
      if (result.status === "approved") {
        setFeedback("Approved! Loading…");
        await refresh();
      } else {
        setFeedback(
          inviteCode
            ? "That invite code wasn't valid — your request is queued for manual approval."
            : "Your request is queued. You'll get access once an admin approves you."
        );
      }
    } catch (err) {
      setFeedback(
        err instanceof ApiError
          ? `Error ${err.status}`
          : "Couldn't submit. Try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6 rounded-lg border bg-card p-8">
        <div className="space-y-2">
          <Badge variant="warning">Pending approval</Badge>
          <h1 className="text-2xl font-semibold">You're signed in — almost there</h1>
          <p className="text-sm text-muted-foreground">
            {me?.user.email ? (
              <>
                Signed in as <strong>{me.user.email}</strong>.
              </>
            ) : (
              <>You're signed in.</>
            )}{" "}
            One more step: an admin needs to approve your account before you
            can use the app. You can leave them a note below or just refresh
            this page once approval lands.
          </p>
        </div>

        <form className="space-y-4" onSubmit={submitOnboard}>
          <div className="space-y-1.5">
            <Label htmlFor="invite">Invite code (optional)</Label>
            <Input
              id="invite"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="Paste code if you have one"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="msg">Message to admin (optional)</Label>
            <textarea
              id="msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi, I'm…"
              maxLength={500}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit request"}
          </Button>
        </form>

        {feedback && (
          <div className="rounded-md bg-muted px-3 py-2 text-sm">{feedback}</div>
        )}

        <div className="flex items-center justify-between text-sm">
          <Button variant="ghost" size="sm" onClick={refresh}>
            Refresh status
          </Button>
          <Button variant="link" size="sm" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
